import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import type { FilterValue } from "@/components/filter-popover";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/empty-state";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAccountActivities } from "@/hooks/api/use-account-activities";
import { useAccountOpportunities } from "@/hooks/api/use-account-opportunities";
import { useAccountsByIds } from "@/hooks/api/use-accounts";
import { useMilestoneAnalyses } from "@/hooks/api/use-milestone-analyses";
import { useMilestoneAnalysisAdminEnabled } from "@/hooks/auth/use-permissions-gates";
import { useAccountSelection } from "@/hooks/use-account-selection";
import { useBuyingGroupSelection } from "@/hooks/use-buying-group-selection";
import { useFilteredActivities } from "@/hooks/use-filtered-activities";
import { usePageTitle } from "@/hooks/use-page-title";
import { cn } from "@/lib/utils";
import { AccountDetailsDialog } from "./account-details-dialog";
import { AccountSelector } from "./account-selector";
import { BuyingGroupsSelector } from "./buying-groups-selector";
import { MilestoneAnalysesBadge } from "./milestone-analyses/milestone-analyses-badge";
import MilestoneAnalysesView from "./milestone-analyses/milestone-analyses-view";
import { ActivityCharts } from "./minimap/activity-charts";
import { ActivityTable } from "./touchpoints-table";
import type { ActivityTableRef, FilterMode } from "./touchpoints-table/types";

interface AccountActivityPageProps {
  initialTab?: "story-explorer" | "milestone-analyses";
}

export default function AccountActivityPage({
  initialTab = "story-explorer",
}: AccountActivityPageProps) {
  // State management
  const [filterMode, setFilterMode] = useState<FilterMode>("all");
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [visibleRange, setVisibleRange] = useState<{
    start: string;
    end: string;
  }>();
  const { selectedAccountId } = useAccountSelection();
  const activityTableRef = useRef<ActivityTableRef>(null);
  const { data: accounts } = useAccountsByIds(
    selectedAccountId ? [selectedAccountId] : undefined,
  );
  const selectedAccount = accounts?.results?.[0];
  // Feature Flags
  const isAdminFeaturesEnabled = useMilestoneAnalysisAdminEnabled();

  // Set page title based on selected account
  usePageTitle(
    selectedAccount
      ? `${selectedAccount.name} - Account Story`
      : "Account Story Viewer",
  );

  const [filters, setFilters] = useState<Record<string, FilterValue>>({});
  const [activeTab, setActiveTab] = useState<
    "story-explorer" | "milestone-analyses"
  >(initialTab);
  const navigate = useNavigate();
  const [customTimeRange, setCustomTimeRange] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  const memoizedFilters = useMemo(() => filters, [filters]);

  // Custom hooks for data fetching and state management
  const {
    selectedOpportunityIds,
    selectOpportunities,
    selectedPersonIds,
    isCustomMode,
    selectAll: resetBuyingGroupSelection,
    buyingGroupMode,
    activeCustomGroupOpportunityIds,
  } = useBuyingGroupSelection();

  const { data: activities, isLoading } = useAccountActivities(
    selectedAccountId,
    selectedOpportunityIds.length > 0 ? selectedOpportunityIds : undefined,
    selectedPersonIds.length > 0 ? selectedPersonIds : undefined,
    buyingGroupMode,
  );

  const { data: milestoneTasks } = useMilestoneAnalyses(
    selectedAccountId,
    buyingGroupMode === "custom"
      ? activeCustomGroupOpportunityIds
      : selectedOpportunityIds.length > 0
        ? selectedOpportunityIds
        : undefined,
    buyingGroupMode,
    true,
  );

  // Use regular filters, server-side filtering by person IDs is now handled in the API
  const filteredActivities = useFilteredActivities(activities, filters);

  // Create a filtered activities variable that respects the cited touchpoints filter when active
  const filteredActivitiesWithCitations = useMemo(() => {
    if (filters.citedTouchpointIds) {
      // If citedTouchpointIds filter is active, pass only those activities
      return (
        activities?.filter((act) =>
          (filters.citedTouchpointIds as string[]).includes(act.id),
        ) ?? []
      );
    } else {
      // Otherwise, use the existing logic based on filterMode
      return filterMode !== "all" ? filteredActivities : undefined;
    }
  }, [activities, filteredActivities, filters.citedTouchpointIds, filterMode]);

  // Refs
  const previousAccountIdRef = useRef<string | undefined>(selectedAccountId);

  const { data: selectedOpportunities } = useAccountOpportunities(
    selectedAccountId,
    selectedOpportunityIds.length ? selectedOpportunityIds : undefined,
    selectedPersonIds.length > 0 ? selectedPersonIds : undefined,
    buyingGroupMode,
    buyingGroupMode === "custom" ? activeCustomGroupOpportunityIds : undefined,
  );

  // When exactly one buying group is selected, center the table on that opportunity's creation date
  useEffect(() => {
    if (
      selectedOpportunityIds.length === 1 &&
      !isLoading &&
      selectedOpportunities &&
      selectedOpportunities.length === 1 &&
      activityTableRef.current
    ) {
      const targetDate = selectedOpportunities[0].created_date;

      if (targetDate) {
        // Ensure the table has rendered with the filtered data before scrolling
        setTimeout(() => {
          activityTableRef.current?.scrollToDate(targetDate);
        }, 0);
      }
    }
  }, [selectedOpportunityIds, selectedOpportunities, isLoading]);

  // Scroll to first cited touchpoint when the 'Highlight in story' action is triggered
  useEffect(() => {
    const citedIds = memoizedFilters.citedTouchpointIds as string[] | undefined;

    if (
      activeTab !== "story-explorer" ||
      !citedIds ||
      citedIds.length === 0 ||
      !activities ||
      activities.length === 0
    ) {
      return;
    }

    const targetDate = activities
      .filter((act) => citedIds.includes(act.id))
      .map((act) => act.date)
      .sort()[0];

    if (!targetDate) return;

    let attempts = 0;
    const maxAttempts = 10;

    const tryScroll = () => {
      if (activityTableRef.current) {
        const result = activityTableRef.current.scrollToDate(targetDate);
        if (result) return; // success
      }
      if (attempts < maxAttempts) {
        attempts += 1;
        setTimeout(tryScroll, 100);
      }
    };

    // initial slight delay to allow the Story Explorer DOM to mount
    const timer = setTimeout(tryScroll, 0);

    return () => clearTimeout(timer);
  }, [memoizedFilters.citedTouchpointIds, activities, activeTab]);

  // Filter handlers
  const updateFilter = useCallback(
    (key: string, value: FilterValue) => {
      setFilters((prev) => {
        if (value === null || value === undefined) {
          // Remove the key entirely when value is null/undefined
          // This prevents storing { [key]: null } in the filters object
          const newFilters = { ...prev };
          delete newFilters[key];
          return newFilters;
        }

        return { ...prev, [key]: value };
      });

      if (filterMode === "all" && value) {
        setFilterMode("highlight");
      }
    },
    [filterMode],
  );

  const clearFilter = useCallback((key: string) => {
    setFilters((prev) => {
      const newFilters = { ...prev };
      delete newFilters[key];
      return newFilters;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({});
  }, []);

  const resetFilters = useCallback((keepPeople?: string[]) => {
    if (keepPeople?.length) {
      setFilters({ people: keepPeople });
    } else {
      setFilters({});
    }
  }, []);

  const handleReset = useCallback(() => {
    resetFilters();
    setFilterMode("all");
    setCustomTimeRange(null);
  }, [resetFilters]);

  // Event handlers
  const handleDateClick = useCallback((date: string) => {
    activityTableRef.current?.scrollToDate(date);
  }, []);

  const handleVisibleRangeChange = useCallback(
    (range: { start: string; end: string }) => {
      setVisibleRange(range);
    },
    [],
  );

  const handlePersonClick = useCallback(
    (personIds: string) => {
      const base = `/accounts/${selectedAccountId}/explorer`;

      // This preserves the current opportunityId in the URL as it's handled by the hook
      navigate(
        {
          pathname: base,
        },
        { replace: true },
      );
      setFilterMode("highlight");
      const personIdArray = personIds.split(",");
      updateFilter("people", personIdArray);
    },
    [updateFilter, selectedAccountId, navigate],
  );

  const handleOpportunitySelect = useCallback(
    (opportunityId: string) => {
      const base = `/accounts/${selectedAccountId}/explorer`;

      // Note: We don't need to manually add the opportunityId parameter
      // as the useBuyingGroupSelection hook will handle this

      navigate(
        {
          pathname: base,
        },
        { replace: true },
      );
      selectOpportunities([opportunityId]);
      setIsDetailsOpen(false);
      handleReset();
    },
    [selectOpportunities, handleReset, selectedAccountId, navigate],
  );

  // Handler specifically for filtering cited touchpoints from analyses
  const [pendingMilestoneTaskId, setPendingMilestoneTaskId] = useState<
    string | null
  >(null);

  const handleFilterCitedTouchpoints = useCallback(
    (taskId: string, touchpointIds: string[]) => {
      // Clear any existing filters first
      clearAllFilters();
      // Update the filter state with a special key that means "show only these IDs"
      updateFilter("citedTouchpointIds", touchpointIds);
      // Switch back to the Story Explorer tab to show the filtered table
      const base = `/accounts/${selectedAccountId}/explorer`;

      // This preserves the current opportunityId in the URL as it's handled by the hook
      navigate(
        {
          pathname: base,
        },
        { replace: true },
      );
      setActiveTab("story-explorer");
      // Store the task ID until the ActivityTable is mounted
      setPendingMilestoneTaskId(taskId);
    },
    [clearAllFilters, updateFilter, selectedAccountId, navigate],
  );

  // Side effects
  useEffect(() => {
    if (selectedAccountId !== previousAccountIdRef.current) {
      handleReset();
      resetBuyingGroupSelection();
      previousAccountIdRef.current = selectedAccountId;
    }
  }, [selectedAccountId, handleReset, resetBuyingGroupSelection]);

  // Sync URL with tab state on initial load
  // Add a ref to prevent multiple tab syncs
  const initialSyncDoneRef = useRef(false);

  useEffect(() => {
    // Only do the initial sync once
    if (!initialSyncDoneRef.current && selectedAccountId) {
      if (initialTab !== activeTab) {
        // Set the tab to match the URL path
        setActiveTab(initialTab);
      }

      // Mark sync as done
      initialSyncDoneRef.current = true;
    }
  }, [selectedAccountId, initialTab, activeTab]);

  // Handle tab change
  const [searchParams] = useSearchParams();

  const handleTabChange = useCallback(
    (value: string) => {
      const newTab = value as "story-explorer" | "milestone-analyses";

      // Skip if the tab hasn't actually changed
      if (newTab === activeTab) {
        return;
      }

      // First update local state
      setActiveTab(newTab);

      // Then update URL if needed
      if (selectedAccountId) {
        const base = `/accounts/${selectedAccountId}`;
        const path =
          newTab === "story-explorer"
            ? `${base}/explorer`
            : `${base}/milestone-analyses`;

        const currentSearchParams = searchParams.toString();

        // Use navigate to change pathname, explicitly preserving search params
        navigate(
          {
            pathname: path,
            search: currentSearchParams ? `?${currentSearchParams}` : "",
          },
          { replace: true },
        );
      }
    },
    [selectedAccountId, navigate, searchParams, activeTab],
  );

  const [pendingScrollDate, setPendingScrollDate] = useState<string | null>(
    null,
  );

  // Open milestone analysis once the table is mounted and active
  useEffect(() => {
    if (
      !pendingMilestoneTaskId ||
      activeTab !== "story-explorer" ||
      isLoading
    ) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 15;

    const tryOpen = () => {
      if (activityTableRef.current && pendingMilestoneTaskId) {
        activityTableRef.current.openMilestoneAnalysis(pendingMilestoneTaskId);
        setPendingMilestoneTaskId(null);
        return;
      }
      if (attempts < maxAttempts) {
        attempts += 1;
        retryTimeout = window.setTimeout(tryOpen, 100);
      } else {
        setPendingMilestoneTaskId(null);
      }
    };

    let retryTimeout: number | null = null;
    retryTimeout = window.setTimeout(tryOpen, 0);

    return () => {
      if (retryTimeout !== null) clearTimeout(retryTimeout);
    };
  }, [pendingMilestoneTaskId, activeTab, isLoading]);

  // Perform pending scroll after new data is rendered (for multi/all selection changes)
  useEffect(() => {
    if (
      !pendingScrollDate ||
      activeTab !== "story-explorer" ||
      isLoading ||
      !activities ||
      !activities.length
    ) {
      return;
    }

    let attempts = 0;
    const maxAttempts = 15;

    const tryScroll = () => {
      if (activityTableRef.current && pendingScrollDate) {
        const result = activityTableRef.current.scrollToDate(pendingScrollDate);
        if (result) {
          setPendingScrollDate(null); // done
          return;
        }
      }
      if (attempts < maxAttempts) {
        attempts += 1;
        retryTimeout = window.setTimeout(tryScroll, 100);
      } else {
        // give up after max attempts
        setPendingScrollDate(null);
      }
    };

    let retryTimeout: number | null = null;
    // slight delay to allow table to mount
    retryTimeout = window.setTimeout(tryScroll, 0);

    return () => {
      if (retryTimeout !== null) clearTimeout(retryTimeout);
    };
  }, [pendingScrollDate, activeTab, isLoading, activities]);

  // Render
  return (
    <div className="flex flex-col h-full space-y-2 overflow-hidden">
      <div className="shrink-0">
        <div className="flex flex-col gap-3 pt-4 lg:flex-row lg:items-center lg:gap-4">
          <h1 className="whitespace-nowrap font-heading shrink-0">
            Account Explorer
          </h1>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4 md:flex-wrap">
            <AccountSelector />
            <BuyingGroupsSelector accountId={selectedAccountId} />
            <div className="flex items-center gap-3 shrink-0">
              <Button
                variant="ghost"
                disabled={!selectedAccountId}
                onClick={() => setIsDetailsOpen(true)}
                className="text-sm text-muted-foreground hover:text-primary hover:bg-accent"
              >
                View details
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AccountDetailsDialog
        isOpen={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        accountId={selectedAccountId}
        onPersonClick={handlePersonClick}
        onDateClick={handleDateClick}
        onOpportunitySelect={handleOpportunitySelect}
      />

      {selectedAccountId ? (
        <Tabs
          defaultValue="story-explorer"
          className="flex-1 min-h-0 flex flex-col"
          value={activeTab}
          onValueChange={handleTabChange}
        >
          <TabsList className="w-auto">
            <TabsTrigger value="story-explorer">Story Explorer</TabsTrigger>
            <TabsTrigger value="milestone-analyses" className="relative">
              <span className="flex items-center">
                Milestone Analyses
                <MilestoneAnalysesBadge
                  milestoneTasks={milestoneTasks}
                  isAdminFeaturesEnabled={isAdminFeaturesEnabled}
                />
              </span>
            </TabsTrigger>
          </TabsList>

          <TabsContent
            value="story-explorer"
            className={cn(
              "mt-1 flex-1 min-h-0 flex flex-col data-[state=inactive]:hidden",
              activeTab !== "story-explorer" &&
                "invisible absolute left-[-9999px] top-0 w-full",
            )}
          >
            <div className="shrink-0">
              <ActivityCharts
                accountId={selectedAccountId}
                onDateClick={handleDateClick}
                onPersonClick={handlePersonClick}
                visibleRange={visibleRange}
                filteredActivities={filteredActivitiesWithCitations}
                selectedOpportunityIds={selectedOpportunityIds}
                selectedPersonIds={isCustomMode ? selectedPersonIds : undefined}
                customTimeRange={customTimeRange}
                setCustomTimeRange={setCustomTimeRange}
                customGroupOpportunityIds={
                  isCustomMode ? activeCustomGroupOpportunityIds : undefined
                }
              />
            </div>
            <ActivityTable
              ref={activityTableRef}
              key={selectedAccountId}
              data={activities}
              filteredActivities={filteredActivitiesWithCitations}
              isLoading={isLoading}
              accountId={selectedAccountId}
              selectedOpportunityIds={selectedOpportunityIds}
              filterMode={filterMode}
              setFilterMode={setFilterMode}
              filters={filters}
              updateFilter={updateFilter}
              clearFilter={clearFilter}
              clearAllFilters={clearAllFilters}
              onVisibleRangeChange={handleVisibleRangeChange}
              totalTouchpoints={activities?.length || 0}
              milestoneTasks={milestoneTasks}
              customGroupOpportunityIds={
                isCustomMode ? activeCustomGroupOpportunityIds : undefined
              }
            />
          </TabsContent>

          <TabsContent
            value="milestone-analyses"
            className={cn(
              "mt-1 flex-1 min-h-0 flex flex-col gap-2 data-[state=inactive]:hidden",
              activeTab !== "milestone-analyses" &&
                "invisible absolute left-[-9999px] top-0 w-full",
            )}
          >
            <MilestoneAnalysesView
              accountId={selectedAccountId}
              selectedOpportunityIds={selectedOpportunityIds}
              onFilterCitedTouchpoints={handleFilterCitedTouchpoints}
            />
          </TabsContent>
        </Tabs>
      ) : (
        <div className="flex-1">
          <EmptyState
            title="Select an account to explore"
            description="Choose an account to view its story and milestone analyses."
          />
        </div>
      )}
    </div>
  );
}
